import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormPage from '../../components/ui/FormPage.tsx';
import OtpInput from '../../components/auth/OtpInput.tsx';
import {
  confirmMfaSetup,
  MfaSetupPayload,
  startMfaSetup,
  storeBackupCodesOnce,
} from '../../services/mfa.ts';

const MfaSetup: React.FC = () => {
  const navigate = useNavigate();
  const [setup, setSetup] = useState<MfaSetupPayload | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await startMfaSetup();
        if (!cancelled) setSetup(data);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Erro ao iniciar setup MFA');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const copySecret = async () => {
    if (!setup?.secret) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Informe o código de 6 dígitos do autenticador.');
      return;
    }
    try {
      setBusy(true);
      setError('');
      const result = await confirmMfaSetup(code);
      storeBackupCodesOnce(result.backupCodes);
      navigate('/settings/security/mfa/backup', { state: { from: 'setup' } });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Código inválido.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormPage
      backTo="/settings/security"
      backLabel="Voltar à segurança"
      breadcrumb="Config / Preferências / Segurança"
      title="Ativar autenticação em duas etapas"
      subtitle="Escaneie o QR no autenticador e confirme com um código"
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/settings/security')}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="mfa-setup-form"
            className="btn btn-primary"
            disabled={busy || loading || !setup}
          >
            {busy ? 'Ativando…' : 'Ativar MFA'}
          </button>
        </>
      }
    >
      <div className="mfa-steps" style={{ marginBottom: 18 }}>
        <div className="mfa-step active">
          <span className="mfa-step-num">1</span> Escanear QR
        </div>
        <div className="mfa-step">
          <span className="mfa-step-num">2</span> Confirmar código
        </div>
        <div className="mfa-step">
          <span className="mfa-step-num">3</span> Códigos de backup
        </div>
      </div>

      {loading && <div className="hint-banner">Gerando QR e chave secreta…</div>}
      {error && (
        <div className="auth-error" style={{ marginBottom: 14 }}>
          <strong>Atenção</strong>
          {error}
        </div>
      )}

      {setup && (
        <form id="mfa-setup-form" onSubmit={handleActivate}>
          <div className="hint-banner">
            Use Google Authenticator, Authy, 1Password ou outro app TOTP.
          </div>
          <div className="form-section-title">1. Escaneie o QR code</div>
          <div className="qr-block">
            <div
              className="qr-placeholder"
              style={{ backgroundImage: 'none', borderStyle: 'solid', padding: 8 }}
            >
              <img
                src={setup.qrDataUrl}
                alt="QR code MFA"
                width={164}
                height={164}
                style={{ display: 'block' }}
              />
            </div>
            <div className="secret-box">
              <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--slate-600)' }}>
                Ou digite a chave secreta
              </label>
              <div className="secret-code">
                <code>{setup.secretFormatted}</code>
                <button type="button" className="btn btn-secondary btn-sm" onClick={copySecret}>
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
              <p className="field-hint" style={{ marginTop: 10 }}>
                Guarde esta chave em local seguro. Ela permite configurar o autenticador sem o QR.
              </p>
            </div>
          </div>

          <div className="form-section-title">2. Confirme com um código de 6 dígitos</div>
          <div className="form-grid">
            <div className="field full">
              <label>Código do autenticador</label>
              <OtpInput value={code} onChange={setCode} disabled={busy} error={!!error} />
            </div>
          </div>
        </form>
      )}
    </FormPage>
  );
};

export default MfaSetup;
